<template>
  <div>
    <div v-if="accessDenied" class="row m-4 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0 text-center text-muted py-3">
          {{ helper.trans('statistic.dashboardPlus.access_denied') }}
        </div>
      </div>
    </div>
    <template v-else>
      <div v-if="isAdmin" class="row m-4 mt-0 mb-0">
        <div class="col-sm-12">
          <div class="card card-body mb-0">
            <div class="row">
              <div class="form-group mb-0 col-md-3 col-lg-3 col-xl-2">
                <TeamLeadMultiselect :multiple="false" v-model="teamLeadFilter"></TeamLeadMultiselect>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row m-4 mt-0">
        <div v-for="block in blocks" :key="block.id" class="col-lg-6 pe-lg-3 mb-3">
          <LoaderWrapper :ref="(el) => setLoader(block.id, el)" class="h-100">
            <div class="card card-body mb-0 h-100">
              <h6 class="text-capitalize-first mb-3">{{ helper.trans(`statistic.dashboardPlus.blocks.${block.id}`) }}</h6>
              <div v-if="!block.rows.length" class="text-center text-muted py-3">
                {{ helper.trans('statistic.dashboardPlus.empty') }}
              </div>
              <div v-else class="overflow-auto">
                <table class="table table-striped border-light align-middle text-left text-nowrap position-relative mb-0">
                  <thead>
                    <tr>
                      <th
                        v-for="column in block.columns"
                        :key="column.key"
                        class="text-capitalize-first"
                        :class="column.align === 'end' ? 'text-end' : ''"
                      >
                        {{ helper.trans(`statistic.dashboardPlus.columns.${column.key}`) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in block.rows" :key="`${block.id}-${row.traderId}-${index}`">
                      <td
                        v-for="column in block.columns"
                        :key="column.key"
                        :class="column.align === 'end' ? 'text-end' : ''"
                      >
                        {{ column.value(row) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </LoaderWrapper>
        </div>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
import {computed, defineComponent, onMounted, reactive, toRefs, watch} from "vue";
import {useStore} from "vuex";
import axios from "axios";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import TeamLeadMultiselect from "@/components/TeamLeadMultiselect.vue";
import {api} from "@/service/APIClient";
import {helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {dateHelper} from "@/service/DateHelper";
import UserDTO from "@/models/UserDTO";
import MoneyDTO from "@/models/MoneyDTO";
import {
  DashboardPlusBlockedTraderDTO,
  DashboardPlusDisputeRateDTO,
  DashboardPlusPayoutDisputesDTO,
  DashboardPlusRejectedDisputesDTO,
  DashboardPlusTeamAcceptanceDTO,
  DashboardPlusWorstConversionDTO,
} from "@/models/DashboardPlusDTO";

type BlockId =
  | 'worstConversion'
  | 'rejectedDisputes'
  | 'payoutDisputes'
  | 'teamAcceptance'
  | 'disputeRate'
  | 'blockedTraders'

interface TableColumn {
  key: string
  align?: 'end'
  value: (row: Record<string, any>) => string | number | null
}

interface TableBlock {
  id: BlockId
  columns: TableColumn[]
  rows: Record<string, any>[]
}

export default defineComponent({
  components: {
    LoaderWrapper,
    TeamLeadMultiselect,
  },
  setup() {
    const store = useStore()
    const loaders = {} as Record<BlockId, InstanceType<typeof LoaderWrapper> | null>
    const state = reactive({
      accessDenied: false,
      teamLeadFilter: null as UserDTO | null,
      worstConversion: [] as DashboardPlusWorstConversionDTO[],
      rejectedDisputes: [] as DashboardPlusRejectedDisputesDTO[],
      payoutDisputes: [] as DashboardPlusPayoutDisputesDTO[],
      teamAcceptance: [] as DashboardPlusTeamAcceptanceDTO[],
      disputeRate: [] as DashboardPlusDisputeRateDTO[],
      blockedTraders: [] as DashboardPlusBlockedTraderDTO[],
    })

    const isAdmin = computed(() => store.getters.isAdmin)

    const setLoader = (id: BlockId, el: unknown) => {
      loaders[id] = el as InstanceType<typeof LoaderWrapper> | null
    }

    const formatPercent = (value: number | null | undefined) => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return ''
      }
      return `${numberFormatter.formatNumber(Number(value), 1)}%`
    }

    const formatMoney = (value?: MoneyDTO) => {
      return value ? numberFormatter.formatMoney(value) : ''
    }

    const formatRewardFees = (fees?: string[]) => {
      if (!fees?.length) {
        return ''
      }
      return fees.map((fee) => `${helper.floatToPercent(fee)}%`).join(', ')
    }

    const requestParams = () => {
      if (isAdmin.value && state.teamLeadFilter?.id) {
        return {teamLeadId: state.teamLeadFilter.id}
      }
      return {}
    }

    const resetItems = () => {
      state.worstConversion = []
      state.rejectedDisputes = []
      state.payoutDisputes = []
      state.teamAcceptance = []
      state.disputeRate = []
      state.blockedTraders = []
    }

    const loadBlock = async <T,>(id: BlockId, request: () => Promise<T[]>, assign: (items: T[]) => void) => {
      loaders[id]?.show()
      try {
        assign(await request())
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          state.accessDenied = true
          return
        }
        assign([])
      } finally {
        loaders[id]?.hide()
      }
    }

    const loadAll = async () => {
      state.accessDenied = false
      if (isAdmin.value && !state.teamLeadFilter?.id) {
        resetItems()
        return
      }

      const params = requestParams()
      await Promise.allSettled([
        loadBlock('worstConversion', () => api.call.statistic.dashboardPlus.worstConversion(params), (items) => {
          state.worstConversion = items
        }),
        loadBlock('rejectedDisputes', () => api.call.statistic.dashboardPlus.rejectedDisputes(params), (items) => {
          state.rejectedDisputes = items
        }),
        loadBlock('payoutDisputes', () => api.call.statistic.dashboardPlus.payoutDisputes(params), (items) => {
          state.payoutDisputes = items
        }),
        loadBlock('teamAcceptance', () => api.call.statistic.dashboardPlus.teamAcceptance(params), (items) => {
          state.teamAcceptance = items
        }),
        loadBlock('disputeRate', () => api.call.statistic.dashboardPlus.disputeRate(params), (items) => {
          state.disputeRate = items
        }),
        loadBlock('blockedTraders', () => api.call.statistic.dashboardPlus.blockedTraders(params), (items) => {
          state.blockedTraders = items
        }),
      ])
    }

    const blocks = computed<TableBlock[]>(() => [
      {
        id: 'worstConversion',
        rows: state.worstConversion,
        columns: [
          {key: 'traderName', value: (row) => String(row.traderName ?? '')},
          {key: 'traderId', value: (row) => row.traderId},
          {key: 'conversion', align: 'end', value: (row) => formatPercent(Number(row.conversion))},
          {key: 'failedCount', align: 'end', value: (row) => Number(row.failedCount)},
          {key: 'totalCount', align: 'end', value: (row) => Number(row.totalCount)},
        ],
      },
      {
        id: 'rejectedDisputes',
        rows: state.rejectedDisputes,
        columns: [
          {key: 'traderName', value: (row) => String(row.traderName ?? '')},
          {key: 'traderId', value: (row) => row.traderId},
          {key: 'canceledCount', align: 'end', value: (row) => Number(row.canceledCount)},
          {key: 'canceledAmountUsdt', align: 'end', value: (row) => formatMoney(row.canceledAmountUsdt as MoneyDTO)},
        ],
      },
      {
        id: 'payoutDisputes',
        rows: state.payoutDisputes,
        columns: [
          {key: 'traderName', value: (row) => String(row.traderName ?? '')},
          {key: 'id', value: (row) => row.traderId},
          {key: 'disputeCount', align: 'end', value: (row) => Number(row.disputeCount)},
          {key: 'amountRub', align: 'end', value: (row) => formatMoney(row.amountRub as MoneyDTO)},
          {key: 'amountUsdt', align: 'end', value: (row) => formatMoney(row.amountUsdt as MoneyDTO)},
        ],
      },
      {
        id: 'teamAcceptance',
        rows: state.teamAcceptance,
        columns: [
          {key: 'traderName', value: (row) => String(row.traderName ?? '')},
          {key: 'id', value: (row) => row.traderId},
          {key: 'dealsCount', align: 'end', value: (row) => Number(row.dealsCount)},
          {key: 'acceptanceAmountRub', align: 'end', value: (row) => formatMoney(row.acceptanceAmountRub as MoneyDTO)},
        ],
      },
      {
        id: 'disputeRate',
        rows: state.disputeRate,
        columns: [
          {key: 'traderName', value: (row) => String(row.traderName ?? '')},
          {key: 'id', value: (row) => row.traderId},
          {key: 'ordersCount', align: 'end', value: (row) => Number(row.ordersCount)},
          {key: 'disputesCount', align: 'end', value: (row) => Number(row.disputesCount)},
          {key: 'disputesPercent', align: 'end', value: (row) => formatPercent(Number(row.disputesPercent))},
        ],
      },
      {
        id: 'blockedTraders',
        rows: state.blockedTraders,
        columns: [
          {key: 'traderName', value: (row) => String(row.traderName ?? '')},
          {key: 'id', value: (row) => row.traderId},
          {key: 'rewardFees', value: (row) => formatRewardFees(row.rewardFees as string[])},
          {key: 'blockedAt', value: (row) => dateHelper.format((row.blockedAt as string | null) ?? null)},
        ],
      },
    ])

    onMounted(() => {
      loadAll()
    })

    watch(() => state.teamLeadFilter?.id, () => {
      if (isAdmin.value) {
        loadAll()
      }
    })

    return {
      ...toRefs(state),
      isAdmin,
      blocks,
      setLoader,
      helper,
    }
  }
})
</script>
